import { FieldAnswersInput } from '@kanos/domain';
import { Button, TextField } from '@kanos/ui';
import { Card, checkForm, formatDate, FormDialog, QueryContent } from '@kanos/web-core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { contractFieldsQuery, useSaveFieldAnswers, type PlatformContractField } from './queries';

/** The answers as the person writes them: one per line, empty lines left out. */
const linesOf = (text: string) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

/** The answers of one value, one per line - in the order the firms see them. */
function AnswersDialog({ field, onClose }: { field: PlatformContractField; onClose: () => void }) {
  const save = useSaveFieldAnswers();
  const [text, setText] = useState(field.options.join('\n'));
  const [unfit, setUnfit] = useState(false);
  const submit = () => {
    const checked = checkForm(FieldAnswersInput, { options: linesOf(text) });
    setUnfit(checked === null);
    if (checked)
      save.mutate({ name: field.name, options: checked.options }, { onSuccess: onClose });
  };
  return (
    <FormDialog
      title={field.description}
      description={
        field.kind === 'list'
          ? 'Die Kanzleien kreuzen davon an, was passt, und ergänzen Eigenes. Verträge, die schon geschrieben sind, behalten ihren Text.'
          : 'Die Kanzleien wählen eine davon oder schreiben eine individuelle Angabe. Verträge, die schon geschrieben sind, behalten ihren Text.'
      }
      submitLabel="Speichern"
      isPending={save.isPending}
      error={save.error}
      unfit={unfit}
      onSubmit={submit}
      draft={text}
      onClose={onClose}
    >
      <TextField
        label="Antworten"
        placeholder={'z. B. DATEV Unternehmen online\nE-Mail'}
        description="Eine Antwort je Zeile, höchstens 20, jede nur einmal. Ohne Antworten tippt die Kanzlei den Wert frei."
        multiline
        rows={6}
        value={text}
        onChange={setText}
      />
    </FormDialog>
  );
}

/** One value: what it is, where it stands, and its answers - or that it is typed freely. */
function FieldRow({ field, onEdit }: { field: PlatformContractField; onEdit: () => void }) {
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
      <div className="grid min-w-0 flex-1 basis-64 gap-0.5">
        <p className="font-medium">{field.description}</p>
        <p className="text-sm text-muted">
          {field.options.length > 0
            ? field.options.join(' · ')
            : 'Keine Antworten – die Kanzlei tippt den Wert frei.'}
        </p>
        <p className="text-xs text-muted">
          <code>{field.name}</code> · {field.kind === 'list' ? 'Liste' : 'Text'}
          {field.updatedAt && ` · geändert am ${formatDate(field.updatedAt)}`}
        </p>
      </div>
      <Button variant="quiet" size="sm" onPress={onEdit}>
        Antworten ändern<span className="sr-only">: {field.description}</span>
      </Button>
    </li>
  );
}

/**
 * The values the firms type in their contracts (Vertragsbaukasten), kept by the Kanos team:
 * the answers proposed for each, by the document a value first stands in. Each change stands in
 * the platform's audit log.
 */
export function ContractFieldsPage() {
  const fields = useQuery(contractFieldsQuery);
  const [editing, setEditing] = useState<PlatformContractField | null>(null);
  return (
    <Card
      id="platform-contract-fields"
      title="Vertragsangaben"
      description="Werte, die die Kanzleien im Vertragsassistenten selbst angeben. Mit Antworten wählen sie aus – wie in einem Fragebogen – und können immer eine individuelle Angabe machen."
    >
      <QueryContent query={fields}>
        {(list) => {
          const documents = [...new Set(list.map((f) => f.documents[0] ?? ''))];
          return documents.map((doc, i) => (
            <section key={doc} aria-labelledby={`field-doc-${String(i)}`} className="grid gap-2">
              <h3 id={`field-doc-${String(i)}`} className="font-semibold">
                {doc}
              </h3>
              <ul className="divide-y divide-hairline border-y border-hairline">
                {list
                  .filter((f) => (f.documents[0] ?? '') === doc)
                  .map((field) => (
                    <FieldRow key={field.name} field={field} onEdit={() => setEditing(field)} />
                  ))}
              </ul>
            </section>
          ));
        }}
      </QueryContent>
      {editing && <AnswersDialog field={editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}
